<template>
  <el-dialog
    :visible.sync="dialogVisible"
    title="图片裁剪"
    width="800px"
    :before-close="handleCancel"
    append-to-body
  >
    <div class="crop-container">
      <!-- 裁剪区域 -->
      <div class="crop-area">
        <vue-cropper
          ref="cropper"
          :img="imgSrc"
          :output-size="outputSize"
          :output-type="outputType"
          :info="true"
          :full="full"
          :fixed="fixed"
          :fixed-number="fixedNumber"
          :fixed-box="fixedBox"
          :original="original"
          :auto-crop="autoCrop"
          :auto-crop-width="autoCropWidth"
          :auto-crop-height="autoCropHeight"
          :center-box="centerBox"
          :high="high"
          :mode="mode"
          :can-move="currentCanMove"
          :can-move-box="currentCanMoveBox"
          :zoomable="true"
          :scalable="true"
          @real-time="realTime"
        />
      </div>

      <!-- 预览区域 -->
      <div class="preview-area">
        <div class="preview-container">
          <div
            class="preview-img"
            :style="{
              width: previews.w + 'px',
              height: previews.h + 'px',
              overflow: 'hidden',
              margin: '5px',
              zoom:(200/previews.w>1?1:(200/previews.w))
            }"
          >
            <div :style="previews.div">
              <img :src="previews.url" :style="previews.img" alt="" />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 操作按钮 -->
    <div class="crop-actions">
      <!-- 移动模式切换 -->
      <div class="move-mode-switch" style="text-align: center;margin-bottom: 20px;">
        <div class="switch-title">移动模式</div>
        <el-radio-group v-model="moveMode" size="small" @change="handleMoveModeChange">
          <el-radio-button label="image">移动图片</el-radio-button>
          <el-radio-button label="box">移动裁剪框</el-radio-button>
        </el-radio-group>
      </div>
      <div>
        <el-button @click="handleCancel">取消</el-button>
        <el-button type="primary" @click="handleConfirm">确认裁剪</el-button>
      </div>
    </div>
  </el-dialog>
</template>

<script>
import { VueCropper } from 'vue-cropper'

export default {
  name: 'CropDialog',
  components: {
    VueCropper
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    imgFile: {
      type: File,
      default: null
    },
    cropOptions: {
      type: Object,
      default: () => ({
        outputSize: 1,
        outputType: 'png',
        fixed: true,
        fixedNumber: [1, 1]
      })
    }
  },
  data() {
    return {
      dialogVisible: false,
      imgSrc: '',
      // 裁剪组件配置
      outputSize: 1, // 裁剪生成图片的质量
      outputType: 'png', // 裁剪生成图片的格式
      info: true, // 裁剪框的大小信息
      full: false, // 是否输出原图比例的截图
      fixed: false, // 是否开启截图框宽高固定比例
      fixedNumber: [1, 1], // 截图框的宽高比例
      fixedBox: true, // 固定截图框大小 不允许改变
      canMove: true, // 上传图片是否可以移动
      canMoveBox: true, // 截图框能否拖动
      original: false, // 上传图片按照原始比例渲染
      autoCrop: true, // 是否默认生成截图框
      autoCropWidth: 200, // 默认生成截图框宽度
      autoCropHeight: 200, // 默认生成截图框高度
      centerBox: true, // 截图框是否被限制在图片里面
      high: true, // 是否按照设备的dpr 输出等比例图片
      mode: 'contain', // 图片默认渲染方式
      previews: {},
      // 移动模式切换
      moveMode: 'image' // 'image' 或 'box'
    }
  },
  computed: {
    // 根据移动模式计算实际的 can-move 和 can-move-box 值
    currentCanMove() {
      return this.moveMode === 'image'
    },
    currentCanMoveBox() {
      return this.moveMode === 'box'
    }
  },
  watch: {
    visible: {
      handler(val) {
        this.dialogVisible = val
        if (val && this.imgFile) {
          this.loadImage()
        }
      },
      immediate: true
    },
    currentCanMoveBox(val) {
      this.fixedBox = !val
    },
    dialogVisible(val) {
      this.$emit('update:visible', val)
    },
    cropOptions: {
      handler(val) {
        Object.assign(this, val)
      },
      deep: true,
      immediate: true
    }
  },
  methods: {
    // 加载图片
    loadImage() {
      const reader = new FileReader()
      reader.onload = (e) => {
        this.imgSrc = e.target.result
        // 图片加载完成后初始化裁剪框
        this.$nextTick(() => {
          if (this.$refs.cropper) {
            this.$refs.cropper.startCrop()
          }
        })
      }
      reader.readAsDataURL(this.imgFile)
    },

    // 实时预览
    realTime(data) {
      this.previews = data
    },

    // 取消裁剪
    handleCancel() {
      this.dialogVisible = false
      this.$emit('cancel')
    },

    // 移动模式切换处理
    handleMoveModeChange() {
      // 切换移动模式时，重新初始化裁剪框以确保属性生效
      this.$nextTick(() => {
        if (this.$refs.cropper) {
          setTimeout(() => {
            this.$refs.cropper.startCrop()
          }, 1)
        }
      })
    },

    // 确认裁剪
    handleConfirm() {
      this.$refs.cropper.getCropBlob((blob) => {
        // 创建新的文件对象
        const fileName = this.imgFile.name.replace(/\.[^/.]+$/, '') + '_cropped.' + this.outputType
        const croppedFile = new File([blob], fileName, {
          type: `image/${this.outputType}`
        })

        this.$emit('confirm', croppedFile)
        this.dialogVisible = false
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.crop-container {
  display: flex;
  height: 400px;
  gap: 20px;
}

.crop-area {
  flex: 1;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  overflow: hidden;
}

.preview-area {
  width: 200px;
  display: flex;
  flex-direction: column;
}

.preview-title {
  text-align: center;
  margin-bottom: 10px;
  font-weight: bold;
  color: #606266;
}

.preview-container {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  background: #f5f7fa;
}

.move-mode-switch {
  margin-top: 15px;
  padding: 10px;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  background: #f8f9fa;
}

.switch-title {
  font-size: 12px;
  color: #606266;
  margin-bottom: 8px;
  text-align: center;
  font-weight: 500;
}

.crop-actions {
  margin-top: 20px;
  text-align: right;
}
</style>
